import {Component, forwardRef, Inject} from "@angular/core";
import {DialogComponent} from "../bootstrap-modal/dialog.component";
import {DialogService} from "../bootstrap-modal/dialog.service";
import {AssetService} from "./asset.service";
import {ProjectService} from "../project/project.service";
import {WorkflowService} from "../workflow/workflow.service";
import {handleErrors} from "../misc/handle-errors.decorator";
import {FormGroup} from "@angular/forms";
import {ContactModel} from "../contact/contact.model";
import {exposeComponentInDebugMode} from "../misc/expose-component-in-debug-mode.decorator";
import {AccessoryService} from "../accessory/accessory.service";
import {toggleLoading} from "../misc/loading.decorator";
import {get} from "lodash";
import {AssetBundledAccessoryModel, AssetDispositionModel, AssetGqlModel} from "./asset.gql-model";
import {StandaloneAccessoryGqlModel} from "../accessory/standalone-accessory.gql-model";
import {AddressModel} from "../address/address.model";

export interface EditAssetDialogModel {
  assetId: string;
}

@Component({
  templateUrl: "./edit-asset-dialog.template.pug",
})
@exposeComponentInDebugMode()
export class EditAssetDialogComponent extends DialogComponent<EditAssetDialogModel, boolean>
  implements EditAssetDialogModel {
  loading: boolean = true;
  assetId: string;
  targetSystem: string;
  projectId: any;
  result = false;
  page = "details";
  error: Error;

  asset: AssetGqlModel = new AssetGqlModel({});
  form: FormGroup = this.asset.formGroup;

  constructor(
    public dialogService: DialogService,
    @Inject(forwardRef(() => AccessoryService)) private accessoryService: AccessoryService,
    @Inject(forwardRef(() => AssetService)) private assetService: AssetService,
    @Inject(forwardRef(() => ProjectService)) private projectService: ProjectService,
    @Inject(forwardRef(() => WorkflowService)) private workflowService: WorkflowService,
  ) {
    super(dialogService);
  }

  changedDartDevice() {
    if (!this.asset.isFutureFleet) return;

    const dartDevice = this.asset.availableDevices.find(it => it.id === this.asset.deviceId);
    if (!dartDevice) return;

    this.asset.dispositions = [new AssetDispositionModel(dartDevice.disposition)];
    this.asset.dispositionId = get(dartDevice, "disposition.id") as string || null;
    this.asset.dispositionText = get(dartDevice, "disposition.dispositionText") as string || "Unknown Disposition";

    this.asset.bundledAccessories = (dartDevice.bundledAccessories || [])
      .map(it => new AssetBundledAccessoryModel(it));

    for (const accessory of this.asset.bundledAccessories) {
      accessory.changed = true;
    }

    const availableBulkOrderItems = dartDevice.availableBulkOrderItems || [];
    this.asset.availableBulkOrderItems = availableBulkOrderItems;

    if (this.asset.bulkOrderAssetId) {
      if (!availableBulkOrderItems.some(it => it.id === this.asset.bulkOrderAssetId)) {
        this.form.controls["bulkOrderAssetId"].setValue("");
        this.form.controls["bulkOrderAssetId"].markAsDirty();
      }
    }

    const availableProducts = dartDevice.availableProducts || [];
    this.asset.availableProducts = availableProducts;

    if (!availableProducts.some(it => it.productId === this.asset.productId)) {
      this.form.controls["productId"].markAsDirty();
      if (availableProducts.length) {
        const product = availableProducts[availableProducts.length - 1];
        this.form.controls["productId"].setValue(product.productId);
        this.form.controls["productId"].markAsDirty();
        this.asset.productId = product.productId;
        this.asset.modelName = product.modelName;
        this.asset.productNumber = product.productNumber;
      } else {
        this.form.controls["productId"].setValue("");
        this.form.controls["productId"].markAsDirty();
        this.asset.modelName = "Not supported by K2";
        this.asset.productNumber = "Unknown";
        this.asset.productId = "";
      }
    }
  }

  updateBulkOrderData() {
    if (!this.asset.availableBulkOrderItems.length) {
      this.form.controls["useBulkOrdering"].setValue(false);
      this.form.controls["useBulkOrdering"].markAsDirty();
      return;
    }

    const form = this.form.getRawValue();

    if (form.useBulkOrdering) {
      const bulkOrder = this.asset.availableBulkOrderItems.find(it => it.id === form.bulkOrderId);
      if (bulkOrder) {
        this.form.controls["orderNumber"].setValue(bulkOrder.orderNumber);
        this.form.controls["orderDate"].setValue(bulkOrder.orderDate);
        this.form.controls["orderNumber"].markAsDirty();
        this.form.controls["orderDate"].markAsDirty();
      }
    }
  }

  @toggleLoading()
  @handleErrors()
  async update() {
    this.targetSystem = this.workflowService.workflowConfig.targetSystem;
    this.asset = await this.assetService.getAsset(this.assetId);
    this.form = this.asset.formGroup;
  }

  @handleErrors()
  async addStandaloneAccessory() {
    const {accessory, saved} = await this.accessoryService.createAccessory(this.asset.siteId, this.asset.id);

    if (!saved) return;

    accessory.original = false;
    accessory.changed = true;
    accessory.keep = true;

    this.asset.standaloneAccessories.push(accessory);
  }

  @handleErrors()
  async editStandaloneAccessory(originalAccessory: StandaloneAccessoryGqlModel) {
    originalAccessory.availableStandaloneAccessories = this.asset.availableStandaloneAccessories;

    const {accessory, saved} = await this.accessoryService.editAccessory(originalAccessory);

    if (!saved) return;

    accessory.export(originalAccessory);
    originalAccessory.changed = true;
    originalAccessory.keep = true;
  }

  removeStandaloneAccessory(accessory: StandaloneAccessoryGqlModel) {
    if (accessory.original) {
      accessory.keep = false;
      return;
    }

    const index = this.asset.standaloneAccessories.indexOf(accessory);
    if (index >= 0) {
      this.asset.standaloneAccessories.splice(index, 1);
    } else {
      accessory.keep = false;
    }
  }

  ready() {
    this.update();
  }

  @handleErrors()
  async editContact(type: "toner" | "breakfix" | "installation"): Promise<ContactModel> {
    const contact = await this.assetService.editAssetContact(this.assetId, type);

    if (Object.prototype.hasOwnProperty.call(this.asset, `${type}Contact`)) {
      this.asset[`${type}Contact`] = contact;
    }

    return contact;
  }

  @handleErrors()
  async editAddress(type: "service" | "toner"): Promise<AddressModel> {
    const address = await this.assetService.editAssetAddress(this.assetId, `asset_${type}`);

    if (Object.prototype.hasOwnProperty.call(this.asset, `${type}Address`)) {
      this.asset[`${type}Address`] = address;
    }

    return address;
  }

  @toggleLoading()
  @handleErrors()
  async save(): Promise<boolean> {
    const {updated} = await this.assetService.saveAsset(this.asset);

    this.result = updated;

    this.close();

    return this.result;
  }
}
